// @ts-nocheck
import Dots from '@jo-v1/components/Loading/Dots';
import { type FC, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import useElementEvents from '@core/hooks/cohesion/useElementEvents';
import { selectAllInputs } from '@core/reducers/inputsSlice';
import { removeProgramFromList, selectLeadDelivery } from '@core/reducers/matchesSlice';
import SponsoredResultsPopover from '@core/shared/components/SponsoredResultsPopover';
import type { Qualifications, VoyagerResult } from '@core/ts/results';
import { highLightKeywords, paragraphToSentencesArray } from '@core/utils/strings/programDescriptions';

import AdditionalSchoolInfo from './AdditionalSchoolInfo';
import DecisionFactorsFlags from './DecisionFactorsFlags';
import styles from './ProgramSelect.module.css';
import KEYWORDS from './RecommenderCard/keywords.json';
import SchoolInfoSection from './SchoolInfoSection';

interface ProgramPanelProps {
  programSnippet: string;
}

export const ProgramPanel = ({ programSnippet }: ProgramPanelProps) => {
  const programSnippetWithHighlightedKeywords = highLightKeywords(programSnippet, KEYWORDS);
  const itemsList = paragraphToSentencesArray(programSnippetWithHighlightedKeywords);
  return (
    <div className={styles.programPanel}>
      <div>Why Consider This Program</div>
      <ul id="programTabPanel" className={styles.programInfoList}>
        {itemsList.map((bullet, idx) => (
          <li key={idx} dangerouslySetInnerHTML={{ __html: bullet }} />
        ))}
      </ul>
    </div>
  );
};

type Props = {
  results: VoyagerResult[];
  setRecommenderStep: (step: string) => void;
  closePopup: () => void;
  eventingOverrides: { [key: string]: any };
  qualifications?: Qualifications;
};

const ProgramSelect: FC<Props> = ({ results, setRecommenderStep, closePopup, eventingOverrides, qualifications }) => {
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();

  // current programs from store
  const { currentPrograms } = useSelector(selectLeadDelivery);

  // inputs state properties
  const inputs = useSelector(selectAllInputs);

  const { elementClicked } = useElementEvents({
    webElement: {
      elementType: 'exit-element',
      text: 'Skip to all results',
      location: eventingOverrides?.location,
      htmlId: 'thank-you-button',
      name: 'skip-recommender',
    },
  });

  const onClose = () => {
    // if there are any selections and user closes the modal, we remove them from the currentPrograms list
    for (const current of currentPrograms) {
      dispatch(removeProgramFromList(current));
    }
    if (closePopup) closePopup();
    elementClicked();
  };

  const singleHit = results.length === 1;

  if (loading) {
    return (
      <section className={styles.loadingContainer}>
        <h2>Loading...</h2>
        <Dots />
      </section>
    );
  }

  const result = results[0];

  return (
    <section className={styles.programSelect}>
      <div className={styles.content}>
        <header>
          <h2 id="recommender_title" className={styles.title}>
            Our Top Recommendation for You:
          </h2>

          {/* <DecisionFactorsFlags decisionFactors={inputs?.importantFactors} /> */}
        </header>

        <div id="recommender_results" className={styles.contentWrapper}>
          <SchoolInfoSection
            index={0}
            setRecommenderStep={setRecommenderStep}
            position={1}
            result={result}
            setLoading={setLoading}
            eventingOverrides={{
              customDimensions: [
                { key: 'isUserRequested', value: 'true' },
                ...(eventingOverrides.customDimensions || []),
              ],
            }}
          />
          <AdditionalSchoolInfo
            fullWidth
            index={0}
            position={1}
            result={result}
            eventingOverrides={{
              location: eventingOverrides?.location,
              customDimensions: [
                { key: 'isUserRequested', value: 'true' },
                ...(eventingOverrides.customDimensions || []),
              ],
            }}
          />
        </div>
      </div>

      <div className={styles.actions}>
        <button type="button" onClick={onClose} className={styles.skipToResults}>
          Skip to all results
        </button>
        <div className={styles.sponsoredResultsPopover}>
          <SponsoredResultsPopover position="above" withIcon={false} />
        </div>
      </div>
    </section>
  );
};

export default ProgramSelect;
